// f:\checkin\frontend\src\screens\LoginAgentScreen\LoginAgentScreen.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../components/Branding/Logo';
import TextInput from '../../components/FormElements/TextInput';

import { useAuth } from '../../contexts/AuthContext'; // Import useAuth
function LoginAgentScreen() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Added isSubmitting state
  const [loginError, setLoginError] = useState(null); // Added loginError state
  const [initialAuthCheckDone, setInitialAuthCheckDone] = useState(false); // State to track if initial auth check is done
  const [initialNavDecisionMade, setInitialNavDecisionMade] = useState(false); // State to track if initial navigation decision has been made
  const [navigationCmd, setNavigationCmd] = useState(null); // State to hold navigation command
  const { login, logout, isAuthenticated, role: authRole } = useAuth(); // user and authIsLoading removed from direct destructuring if not used in effects

  // To track if the component is mounted, useful for async operations in useEffect
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent multiple submissions

    console.log('[LoginAgentScreen handleSubmit] Start. Current isAuthenticated (from useAuth):', isAuthenticated);
    logout(); // Clear any existing session
    console.log('[LoginAgentScreen handleSubmit] Called logout().');

    setIsSubmitting(true); // Set submitting state
    setLoginAttempted(false); // Reset loginAttempted before a new attempt
    setLoginError(null);
    console.log('[LoginAgentScreen handleSubmit] Agent Login attempt with:', formData);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    try {
      const response = await fetch(`${API_BASE_URL}/api/agents/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error || data.message || `Login failed with status: ${response.status}`;
        if (isMounted.current) {
          setLoginError(typeof errorMessage === 'object' ? errorMessage.message : errorMessage);
        }
        throw new Error(typeof errorMessage === 'object' ? errorMessage.message : errorMessage);
      }

      if (data.token && data.role) {
        // Fetch agent details before calling AuthContext.login to pass the full user object
        // This mirrors the pattern in Admin and Supplier login screens for consistency
        console.log(`[LoginAgentScreen handleSubmit] Login API success. Role: ${data.role}. Fetching agent details...`);
        try {
          const agentDetailsResponse = await fetch(`${API_BASE_URL}/api/agents/me`, { // Changed to correct agent details endpoint
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${data.token}`,
            },
          });

          const fetchedAgentData = await agentDetailsResponse.json();

          if (!agentDetailsResponse.ok) {
            const agentErrorMessage = fetchedAgentData.error?.message || fetchedAgentData.message || `Failed to fetch agent details: ${agentDetailsResponse.status}`;
            if (isMounted.current) {
              setLoginError(agentErrorMessage);
            }
            throw new Error(agentErrorMessage); // This will be caught by the outer catch
          }

          console.log('[LoginAgentScreen handleSubmit] Agent details fetched:', fetchedAgentData);

          // Construct agent object as in supplier/admin screens
          const agentObject = fetchedAgentData.agent || fetchedAgentData;

          // Prepare the agent object for AuthContext
          const authAgentObject = {
            ...agentObject,
            image: null, // Default to null
          };

          // Construct image URL if avatar_file_id or similar field exists
          if (agentObject && agentObject.avatar_file_id) {
            authAgentObject.image = `${API_BASE_URL}/files/serve/${agentObject.avatar_file_id}`;
          } else if (agentObject && agentObject.avatar_filepath) {
            authAgentObject.image = agentObject.avatar_filepath;
          }

          // Call context login and wait for it to complete
          await login(data.token, data.role, agentObject.id, authAgentObject);
          console.log(`[LoginAgentScreen handleSubmit] Login completed, checking for preserved location`);

          // Check if there's a preserved location from ProtectedRoute redirect
          const from = location.state?.from?.pathname || location.state?.from;
          if (from && from !== '/login') {
            console.log('[LoginAgentScreen handleSubmit] Found preserved location from ProtectedRoute:', from);
            if (isMounted.current) {
              navigate(from, { replace: true });
            }
          } else {
            console.log('[LoginAgentScreen handleSubmit] No preserved location found, redirecting to /home');
            if (isMounted.current) {
              navigate('/home');
            }
          }

        } catch (fetchAgentError) {
          console.error('[LoginAgentScreen handleSubmit] Error fetching agent details after login:', fetchAgentError.message);
          if (isMounted.current) {
            setLoginError(fetchAgentError.message || 'Failed to fetch agent data after login.');
          }
          // No throw here, error is set, finally will run.
        }
      } else {
        if (isMounted.current) {
          setLoginError('Login successful, but token or role not received from server.');
        }
        // No throw here, error is set, finally will run.
      }
    } catch (error) {
      console.error('[LoginAgentScreen handleSubmit] Catch block error:', error.message);
      if (isMounted.current && !loginError) { // Only set if not already set by specific error handling
        setLoginError(error.message || 'Login failed. Please check your credentials.');
      }
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false); // Reset submitting state
        console.log('[LoginAgentScreen handleSubmit] Finally block. isSubmitting set to false.');
      }
    }
  };

  // Handle auth state changes
  useEffect(() => {
    if (!isMounted.current) return;

    if (isAuthenticated && authRole === 'agent') {
      console.log('[LoginAgentScreen] Authenticated as agent, checking for preserved location');

      // Check if there's a preserved location from ProtectedRoute redirect
      const from = location.state?.from?.pathname || location.state?.from;
      if (from && from !== '/login') {
        console.log('[LoginAgentScreen] Found preserved location from ProtectedRoute:', from);
        navigate(from, { replace: true });
        return;
      }

      console.log('[LoginAgentScreen] No preserved location found, redirecting to /home');
      navigate('/home');
    }
  }, [isAuthenticated, authRole, navigate, location.state]);


  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="col-md-6 form-side">
      <div className="form-container text-center">
        <Logo />
        <h1 className="text-success fw-bold mb-4">Portal de Asesores</h1>
        {loginError && <div className="alert alert-danger" role="alert">{loginError}</div>}
        {/* Subtitle removed to match the cleaner design of LoginAdminScreen */}
        <form onSubmit={handleSubmit} noValidate>
          <TextInput
            type="email"
            id="email"
            name="email"
            placeholder="Email"
            ariaLabel="Email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={isSubmitting}
            iconClass="bi bi-envelope"
            wrapperClassName="mb-3" />
          <TextInput type={showPassword ? "text" : "password"} id="password" name="password" placeholder="Contraseña" ariaLabel="Contraseña" value={formData.password} onChange={handleChange} required disabled={isSubmitting} iconClass={`bi ${showPassword ? 'bi-eye' : 'bi-eye-slash'}`} onIconClick={togglePasswordVisibility} iconAriaLabel="Mostrar u ocultar contraseña" wrapperClassName="mb-0" />
          <div className="text-end mt-2"><Link to="/forgot-password" className="small">¿Olvidaste tu contraseña?</Link></div>
          <div className="buttons-container d-grid gap-3 mt-5 mb-5">
            <button type="submit" className="btn btn-checkin" disabled={isSubmitting}>
              {isSubmitting ? 'Iniciando...' : 'Iniciar sesión'}
            </button>
          </div>
        </form>
        <p className="mt-3 text-muted small">protegido por reCAPTCHA · <a href="#">Privacidad</a> · <a href="#">Condiciones</a></p>
      </div>
    </div>
  );
}

export default LoginAgentScreen;